/**
 * useTrialStatus Hook - Redux-based hook for trial status
 *
 * ARCHITECTURAL PATTERN:
 * Pure Redux selector hook that reads trial status from global state.
 * This hook does NOT handle polling - that is managed centrally by
 * useSubscriptionPolling() which should be called once at the app level.
 *
 * This ensures:
 * - Global accessibility across components
 * - Prevention of duplicate API calls
 * - Single polling timer for entire app (not per component)
 * - Better caching and state management
 * - Consistency with other subscription features
 *
 * USAGE:
 * const { status, loading, error, refresh } = useTrialStatus();
 *
 * NOTE: Background polling is handled by useSubscriptionPolling() in MainLayout.
 * This hook only provides read access and manual refresh capability.
 */

import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/stateHooks';
import {
  fetchMyTrialStatus,
  selectTrialStatus,
  selectTrialLoading,
  selectSubscriptionError,
} from '../redux/slices/subscriptionSliceModel';
import type { TrialStatus } from '../services/subscription.service';

interface UseTrialStatusReturn {
  status: TrialStatus | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export const useTrialStatus = (): UseTrialStatusReturn => {
  const dispatch = useAppDispatch();

  // Select from Redux store
  const status = useAppSelector(selectTrialStatus);
  const loading = useAppSelector(selectTrialLoading);
  const error = useAppSelector(selectSubscriptionError);

  // Refresh function for manual refresh
  const refresh = useCallback(() => {
    dispatch(fetchMyTrialStatus());
  }, [dispatch]);

  return { status, loading, error, refresh };
};
